import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Button } from 'rebass';
import OutsideClickHandler from 'react-outside-click-handler';
import classNames from 'classnames';

import { PositionerV2 } from 'components/Positioner';
import Portal from 'components/Portal';
import InputSearch from 'components/InputSearch';
import Card from 'components/Card';

import styles from './Combobox.module.css';

// Cap rendered options so very large item lists (e.g. ~15k) don't lock up
// the browser. The user narrows further by typing.
const MAX_VISIBLE_OPTIONS = 100;

/**
 * Combobox component. Kind of like a select, but with a search input.
 *
 * @param {{ value: string | number; label: string; disabled?: boolean; description?: string }[]} items List of items (options)
 * @param {string | number} value Currently selected value
 * @param {(value: string | number) => void} onChange Callback when the value changes
 * @param {string} [className]
 * @param {React.CSSProperties} [style]
 * @param {string} [ariaLabel] Aria label for the combobox
 * @param {string} [ariaLabelledBy] DOM id of an element labeling the combobox (alternative to ariaLabel)
 * @param {string} [id] ID for the combobox
 * @param {boolean} [defaultOpen] Open the options as soon as the combobox mounts
 * @param {() => void} [onDismiss] Called when the options close without a selection (Escape, Tab, outside click)
 */
export function Combobox({
    items = [],
    value,
    onChange,
    className,
    style,
    ariaLabel,
    ariaLabelledBy,
    id,
    defaultOpen = false,
    onDismiss
}) {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);

    // Find the currently selected item’s label
    const label = useMemo(() => {
        const selectedItem = items.find(item => item.value === value);
        return selectedItem ? selectedItem.label : '';
    }, [items, value]);

    // Filter options based on the query (full match set — used for the result
    // count and truncation hint).
    // Plain string match — avoids RegExp so special characters like `[` or `(` don't throw.
    const filteredOptions = useMemo(
        () => items.filter(item => item.label.toLowerCase().includes(query.toLowerCase())),
        [items, query]
    );

    // Only render the first N to keep the DOM small. Keyboard navigation and
    // `aria-activedescendant` operate against this same slice.
    const options = useMemo(() => filteredOptions.slice(0, MAX_VISIBLE_OPTIONS), [filteredOptions]);
    const isTruncated = filteredOptions.length > options.length;

    // The popover renders through a Portal into `document.body`, so it is a
    // sibling of the app root rather than a descendant of whatever container
    // set `dir`. CSS direction inherits down the DOM tree, not the React tree,
    // so the panel would fall back to the document direction and render LTR
    // inside an RTL form (see PublicScreenPageFormWrapper).
    //
    // The trigger *is* in the tree, so its computed direction is the
    // authoritative answer; mirror it onto the panel. Read at open time rather
    // than on every render, because that is when the anchor is guaranteed
    // mounted and the value is needed. Same technique as `star-rating.jsx`.
    const panelDirection =
        isOpen && anchorRef.current ? window.getComputedStyle(anchorRef.current).direction : undefined;

    // Closes the dropdown and returns keyboard focus to the trigger button so
    // the user's tab order picks up where it left off instead of falling back
    // to <body>. Used for "completed" close paths (Enter / click / Escape).
    // Tab and outside-click intentionally do NOT call this — the user is
    // already moving focus themselves.
    const closeAndRefocus = () => {
        setIsOpen(false);
        if (anchorRef.current) {
            anchorRef.current.focus({ preventScroll: true });
        }
    };

    // Closes the dropdown without a selection and lets the owner react (e.g.
    // drop a row that only exists while picking).
    const dismiss = () => {
        setIsOpen(false);
        if (onDismiss) onDismiss();
    };

    const handleKeyDown = e => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(0);
            } else {
                setActiveIndex(prev => Math.min(options.length - 1, prev + 1));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(options.length - 1);
            } else {
                setActiveIndex(prev => Math.max(0, prev - 1));
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selectedOption = activeIndex !== null ? options[activeIndex] : options[0];

            if (selectedOption && !selectedOption.disabled) {
                onChange(selectedOption.value);
                setQuery('');
                closeAndRefocus();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            // Escape belongs to the open listbox. The panel is portaled, but
            // React still bubbles the event to the combobox's React ancestors,
            // so without this a combobox inside a modal closes the modal too.
            e.stopPropagation();
            closeAndRefocus();
            if (onDismiss) onDismiss();
        } else if (e.key === 'Tab') {
            dismiss();
        }
    };

    const handleOptionClick = option => {
        if (option.disabled) return;
        onChange(option.value);
        setQuery('');
        closeAndRefocus();
    };

    const search = query => {
        setActiveIndex(null);
        setQuery(query);
    };

    // Opened after mount rather than via the initial state: the panel measures
    // the trigger (`anchorRef.current.offsetWidth`), which only exists once the
    // trigger has rendered. A layout effect, so the closed state never paints.
    useLayoutEffect(() => {
        if (defaultOpen) setIsOpen(true);
    }, []);

    useEffect(() => {
        if (activeIndex !== null && options[activeIndex]) {
            const activeElement = document.getElementById(`option-${options[activeIndex].value}`);
            if (activeElement) {
                activeElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, options]);

    return (
        <>
            <Button
                id={id}
                width="100%"
                display="flex"
                className={classNames('flex items-center justify-between', className)}
                ref={anchorRef}
                variant="secondary-gray"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-describedby="combobox-trigger-instructions"
                onClick={() => setIsOpen(true)}
                style={style}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
            >
                {label || 'Select an option'} <FiChevronDown role="img" aria-label="Arrow down" style={{ margin: 0 }} />
            </Button>
            <div id="combobox-trigger-instructions" className="visually-hidden">
                Press Enter to open the combobox options.
            </div>
            {isOpen && (
                <Portal isOpen={isOpen}>
                    <PositionerV2 anchorRef={anchorRef}>
                        <OutsideClickHandler onOutsideClick={dismiss}>
                            <Card
                                dir={panelDirection}
                                className={styles.card}
                                style={{ width: anchorRef.current.offsetWidth }}
                            >
                                <div className={styles.searchContainer}>
                                    <InputSearch
                                        ref={ref => ref && ref.focus({ preventScroll: true })}
                                        id="combobox-input"
                                        className="nice-underlined nice-dropdown-search"
                                        value={query}
                                        onChange={event => search(event.target.value)}
                                        // This is an in-dropdown filter, not a form
                                        // field — browser history/autofill popups
                                        // overlap the option list, so suppress them.
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        aria-label="Search options"
                                        role="combobox"
                                        aria-owns="combobox-list"
                                        aria-controls="combobox-list"
                                        aria-activedescendant={
                                            activeIndex !== null && options[activeIndex]
                                                ? `option-${options[activeIndex].value}`
                                                : undefined
                                        }
                                        aria-autocomplete="list"
                                        aria-describedby="combobox-instructions"
                                        onKeyDown={handleKeyDown}
                                    />
                                    <div
                                        role="log"
                                        aria-live="polite"
                                        aria-atomic="false"
                                        aria-relevant="additions text"
                                        className="visually-hidden"
                                    >
                                        <div id="aria-results">
                                            {filteredOptions.length === 1
                                                ? `1 result found for search term "${query}".`
                                                : `${filteredOptions.length} results found for search term "${query}".`}
                                            {isTruncated &&
                                                ` Showing first ${options.length}. Keep typing to narrow the list.`}
                                        </div>
                                    </div>
                                    <div id="combobox-instructions" className="visually-hidden">
                                        {filteredOptions.length} result{filteredOptions.length === 1 ? '' : 's'} found.
                                        {isTruncated && ` Showing first ${options.length}.`} Start typing to search. Use
                                        the up and down arrow keys to navigate. Press Enter to select, or Escape to
                                        close.
                                    </div>
                                </div>
                                <div id="combobox-list" role="listbox" className={styles.list}>
                                    {options.length > 0 &&
                                        options.map((option, index) => (
                                            <div
                                                role="option"
                                                aria-selected={option.value === value}
                                                aria-disabled={option.disabled || undefined}
                                                id={`option-${option.value}`}
                                                key={option.value}
                                                tabIndex={-1}
                                                onClick={() => handleOptionClick(option)}
                                                onMouseEnter={() => setActiveIndex(index)}
                                                className={classNames(
                                                    styles.option,
                                                    index === activeIndex && styles.activeOption,
                                                    option.disabled && styles.disabledOption
                                                )}
                                            >
                                                {option.label}
                                                {option.description && (
                                                    <span className={styles.optionDescription}>
                                                        {option.description}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    {isTruncated && (
                                        <div className={styles.empty}>
                                            Showing first {options.length} of {filteredOptions.length}. Keep typing to
                                            narrow the list.
                                        </div>
                                    )}
                                    {filteredOptions.length === 0 && (
                                        <div className={styles.empty}>
                                            <strong>0 results</strong>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </OutsideClickHandler>
                    </PositionerV2>
                </Portal>
            )}
        </>
    );
}
